/**
 * Metric envelope helpers — keep in sync with /app/backend/metrics_envelope.py.
 *
 * Every metric crossing the API boundary in P2.1+ arrives in the envelope
 *   { value, data_origin, as_of_date, source, period_fy,
 *     period_frequency, statement_version, statement_type,
 *     freshness_label? }
 *
 * These helpers turn an envelope into UI-ready labels + badges so we
 * don't sprinkle the same date-formatting logic across components.
 */

/**
 * Format an ISO date string to "30-Jun-2026". Returns null on parse failure.
 */
function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const day  = String(d.getUTCDate()).padStart(2, "0");
    const mon  = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
    const yr   = d.getUTCFullYear();
    return `${day}-${mon}-${yr}`;
  } catch {
    return null;
  }
}

/**
 * Build the user-facing freshness label.
 *   live      → "Live"
 *   snapshot  → "As on 30-Jun-2026 (Snapshot)"
 *   historical→ "Historical · FY24-25 (31-Mar-2025)"
 *
 * Prefers the server-rendered `freshness_label` when present (keeps the
 * two implementations in sync); falls back to client formatting.
 */
export function freshnessLabel(envelope) {
  if (!envelope) return "";
  if (envelope.freshness_label) return envelope.freshness_label;
  const origin = envelope.data_origin;
  const date   = fmtDate(envelope.as_of_date);
  if (origin === "live") return "Live";
  if (origin === "snapshot") return date ? `As on ${date} (Snapshot)` : "Snapshot";
  // historical
  if (envelope.period_fy && date) return `Historical · ${envelope.period_fy} (${date})`;
  if (envelope.period_fy)         return `Historical · ${envelope.period_fy}`;
  if (date)                       return `Historical · ${date}`;
  return "Historical";
}

/**
 * Return tailwind classes for the freshness badge — colour-coded by
 * origin so the user can scan the board and know what's real-time
 * versus what's a snapshot at a glance.
 */
export function freshnessBadgeClasses(envelope) {
  const origin = envelope?.data_origin;
  if (origin === "live")
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (origin === "snapshot")
    return "bg-blue-50 text-blue-700 border-blue-200";
  return "bg-slate-100 text-slate-600 border-slate-200";
}

/**
 * Short "Live" / "Snapshot" / "Historical" word for the badge body.
 * Used when the row is space-constrained and we can't show the full label.
 */
export function originWord(envelope) {
  const o = envelope?.data_origin;
  if (o === "live") return "Live";
  if (o === "snapshot") return "Snapshot";
  return "Historical";
}

/**
 * Format any rupee value using the Indian numbering system.
 * Returns a string; pass through "—" when value is null/undefined.
 */
export function fmtINR(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)} L`;
  return `${sign}₹${abs.toLocaleString("en-IN")}`;
}

/**
 * Format a metric value according to the unit declared in the catalog.
 *   inr   → "₹3.42 L"
 *   pct   → "23.7%"
 *   days  → "62 days"
 *   ratio → "1.43"
 *   score → "74"
 */
export function fmtMetric(value, unit) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  switch (unit) {
    case "inr":   return fmtINR(n);
    case "pct":   return `${n.toFixed(1)}%`;
    case "days":  return `${Math.round(n)} days`;
    case "ratio": return n.toFixed(2);
    case "score": return `${Math.round(n)}`;
    default:      return String(n);
  }
}
